import { useCallback, useEffect, useRef } from "react";

// The popup is position:absolute, so it only moves with a container that is
// its containing block.
const asContainingBlock = (el: HTMLElement) => {
  if (getComputedStyle(el).position === "static") {
    el.style.position = "relative";
  }
  return el;
};

// Antd positions a popup once, when it opens, against its container. With the
// default container (document.body) the popup moves with the page, so it is
// left behind when its trigger moves differently: when the trigger sits in a
// container that scrolls internally (e.g. CORESider's content), or in a
// position:fixed box (e.g. CORESider itself) while the page scrolls. Mounting
// the popup inside that container lets the browser move both together.
export const getPopupParent = (node: HTMLElement): HTMLElement => {
  let overflowAncestor: HTMLElement | undefined;
  let el = node.parentElement;
  while (el && el !== document.body) {
    const style = getComputedStyle(el);
    if (/(auto|scroll)/.test(style.overflowY)) {
      if (el.scrollHeight > el.clientHeight) return asContainingBlock(el);
      overflowAncestor ??= el;
    }
    if (style.position === "fixed") {
      // Prefer an overflow container inside the fixed box: the fixed box may
      // clip its content (CORESider does, for its open/close transition).
      return asContainingBlock(overflowAncestor ?? el);
    }
    el = el.parentElement;
  }
  return document.body;
};

// Popups at antd's default z-index paint over the page's sticky top bar and
// header, even once their trigger has scrolled under them. Given this class, a
// popup opened from the page sits under them instead, as its trigger does (see
// @page-popup-z-index). Not one opened from a layer above them, though (a
// modal, drawer, popover, full screen chart...): it has to stay above that.
export const PAGE_POPUP_CLASS = "core-page-popup";
// The layer ConfigProvider mounts the page's antd popups in.
export const PAGE_POPUP_LAYER_CLASS = "core-page-popup-layer";

export const isInPageContent = (node: HTMLElement) => {
  // Opened from a popup that is itself one of the page's (e.g. a submenu of a
  // dropdown): it stays with that popup, though that popup's own z-index is
  // above @page-popup-z-index within the page's layer.
  if (node.closest(`.${PAGE_POPUP_LAYER_CLASS}, .${PAGE_POPUP_CLASS}`)) {
    return true;
  }
  for (let el = node.parentElement; el && el !== document.body; ) {
    const { position, zIndex } = getComputedStyle(el);
    // Above @page-popup-z-index.
    if (position !== "static" && Number(zIndex) > 998) return false;
    el = el.parentElement;
  }
  return true;
};

// rc-trigger asks for the popup container only once, the first time the popup
// opens, but whether an ancestor scrolls can change afterwards (e.g. a list
// that grows until it overflows). So the popup gets a container of its own,
// which is moved into the right parent each time the popup opens.
export const useScrollablePopupContainer = () => {
  const triggerRef = useRef<HTMLElement>();
  const containerRef = useRef<HTMLDivElement>();

  useEffect(() => () => containerRef.current?.remove(), []);

  const placeContainer = useCallback(() => {
    const trigger = triggerRef.current;
    const container = containerRef.current;
    if (!trigger || !container) return;
    const parent = getPopupParent(trigger);
    if (container.parentElement !== parent) {
      parent.appendChild(container);
    }
    // Mounted in a scroll area too: that isn't a stacking context, so the
    // popup's z-index would still put it above the page's top bar.
    container.classList.toggle(PAGE_POPUP_CLASS, isInPageContent(trigger));
  }, []);

  const getPopupContainer = useCallback(
    (trigger: HTMLElement) => {
      triggerRef.current = trigger;
      containerRef.current ??= document.createElement("div");
      placeContainer();
      return containerRef.current;
    },
    [placeContainer]
  );

  return { getPopupContainer, onOpen: placeContainer };
};
